import { LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { EventLogEntry } from "./app-events.ts";
import type { AppViewState } from "./app-view-state.ts";
import type { DevicePairingList } from "./controllers/devices.ts";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import type { ExecApprovalsFile, ExecApprovalsSnapshot } from "./controllers/exec-approvals.ts";
import type { SkillMessage } from "./controllers/skills.ts";
import type { GatewayBrowserClient, GatewayHelloOk } from "./gateway.ts";
import type { Tab } from "./navigation.ts";
import type { ResolvedTheme, ThemeMode } from "./theme.ts";
import type {
  AgentsListResult,
  AgentsFilesListResult,
  AgentIdentityResult,
  ConfigSnapshot,
  ConfigUiHints,
  CronJob,
  CronRunLogEntry,
  CronStatus,
  HealthSnapshot,
  LogEntry,
  LogLevel,
  PresenceEntry,
  ChannelsStatusSnapshot,
  SessionsListResult,
  SkillStatusReport,
  StatusSummary,
  NostrProfile,
} from "./types.ts";
import type { NostrProfileFormState } from "./views/channels.nostr-profile-form.ts";
import {
  handleChannelConfigReload as handleChannelConfigReloadInternal,
  handleChannelConfigSave as handleChannelConfigSaveInternal,
  handleNostrProfileCancel as handleNostrProfileCancelInternal,
  handleNostrProfileEdit as handleNostrProfileEditInternal,
  handleNostrProfileFieldChange as handleNostrProfileFieldChangeInternal,
  handleNostrProfileImport as handleNostrProfileImportInternal,
  handleNostrProfileSave as handleNostrProfileSaveInternal,
  handleNostrProfileToggleAdvanced as handleNostrProfileToggleAdvancedInternal,
  handleWhatsAppLogout as handleWhatsAppLogoutInternal,
  handleWhatsAppStart as handleWhatsAppStartInternal,
  handleWhatsAppWait as handleWhatsAppWaitInternal,
} from "./app-channels.ts";
import {
  handleAbortChat as handleAbortChatInternal,
  handleSendChat as handleSendChatInternal,
  removeQueuedMessage as removeQueuedMessageInternal,
} from "./app-chat.ts";
import {
  DEFAULT_AGENT_WORKSPACE_PATH,
  DEFAULT_CRON_FORM,
  DEFAULT_LOG_LEVEL_FILTERS,
} from "./app-defaults.ts";
import { connectGateway as connectGatewayInternal } from "./app-gateway.ts";
import {
  handleConnected,
  handleDisconnected,
  handleFirstUpdated,
  handleUpdated,
} from "./app-lifecycle.ts";
import { renderApp } from "./app-render.ts";
import {
  exportLogs as exportLogsInternal,
  handleChatScroll as handleChatScrollInternal,
  handleLogsScroll as handleLogsScrollInternal,
  resetChatScroll as resetChatScrollInternal,
  scheduleChatScroll as scheduleChatScrollInternal,
} from "./app-scroll.ts";
import {
  applySettings as applySettingsInternal,
  loadCron as loadCronInternal,
  loadOverview as loadOverviewInternal,
  setTab as setTabInternal,
  setTheme as setThemeInternal,
  onPopState as onPopStateInternal,
} from "./app-settings.ts";
import {
  resetToolStream as resetToolStreamInternal,
  type ToolStreamEntry,
  type CompactionStatus,
} from "./app-tool-stream.ts";
import { resolveInjectedAssistantIdentity } from "./assistant-identity.ts";
import { loadAssistantIdentity as loadAssistantIdentityInternal } from "./controllers/assistant-identity.ts";
import { loadSettings, type UiSettings } from "./storage.ts";
import { type ChatAttachment, type ChatQueueItem, type CronFormState } from "./ui-types.ts";
import {
  hydratePmosConnectorDraftsFromConfig,
  loadPmosConnectorsStatus,
  loadPmosWorkflowCredentials,
  savePmosConnectorsConfig,
  type PmosConnectorsStatus,
} from "./controllers/pmos-connectors.ts";
import {
  activatePmosModel,
  assignPmosAgentModel,
  clearPmosModelApiKeyForRef,
  deactivatePmosModel,
  deletePmosModel,
  clearPmosModelApiKey,
  loadPmosModelWorkspaceState,
  savePmosModelConfig,
  selectPmosModelEditor,
  setPmosModelProvider,
  upsertPmosModelFromRef,
  type PmosAgentModelAssignment,
  type PmosModelRow,
  type PmosModelProvider,
} from "./controllers/pmos-model-auth.ts";
import {
  adminResetPmosAuthPassword,
  isPmosSignupEnabled,
  changePmosAuthPassword,
  loadPmosAuthSession,
  loginPmosAuth,
  logoutPmosAuth,
  signupPmosAuth,
  type PmosAuthUser,
} from "./controllers/pmos-auth.ts";
import type { PmosExecutionTraceEvent } from "./controllers/pmos-trace.ts";
import {
  hydratePmosAdminFromConfig,
  loadPmosAdminState,
  removePmosMember,
  resetAllWorkspaces,
  restartGateway,
  savePmosAdminState,
  upsertPmosMember,
  type PmosAuditEvent,
  type PmosMember,
  type PmosMemberStatus,
  type PmosRole,
} from "./controllers/pmos-admin.ts";
import {
  approvePmosCommandStep,
  clearPmosCommandHistory,
  executePmosCommandPlan,
  planPmosCommand,
  type PmosCommandHistoryEntry,
  type PmosCommandPendingApproval,
  type PmosCommandPlanStep,
} from "./controllers/pmos-command-center.ts";
import {
  completeProjectTodo,
  createProjectComment,
  createProjectTodo,
  fetchProjectEntityDetail,
  fetchProjectSection,
  loadPmosProjectsSnapshot,
  reopenProjectTodo,
  type PmosProjectCard,
  type PmosProjectDetailTab,
  type PmosEntityDetail,
  type PmosEntityReference,
  type PmosMutationResult,
  type PmosProjectSectionResult,
  type PmosProjectsSnapshot,
  type PmosProjectTodoItem,
} from "./controllers/pmos-projects.ts";

function buildProjectScreenContext(project: PmosProjectCard, tab: PmosProjectDetailTab, data: unknown): string {
  const projectJson = {
    view: "project-detail",
    project: project.name,
    projectId: project.id,
    health: project.health,
    openTodos: project.openTodos,
    assignedTodos: project.assignedTodos,
    overdueTodos: project.overdueTodos,
    dueTodayTodos: project.dueTodayTodos,
    futureTodos: project.futureTodos,
    noDueDateTodos: project.noDueDateTodos,
    todoLists: project.todoLists,
    nextDueOn: project.nextDueOn,
    description: project.description,
    updatedAt: project.updatedAt,
    dockCapabilities: (project.dockCapabilities ?? []).map((dock) => dock.title ?? dock.name).filter(Boolean),
    activeTab: tab,
    appUrl: project.appUrl,
    tabData: null as unknown,
  };

  if (data && Array.isArray(data) && data.length > 0) {
    const today = new Date().toISOString().slice(0, 10);
    if (tab === "todos") {
      type Group = { name: string; todos: Array<{ title: string; status: string | null; dueOn: string | null; assignee: string | null; appUrl: string | null }> };
      const groups = data as Group[];
      projectJson.tabData = groups.map((g) => ({
        listName: g.name,
        openCount: g.todos.filter((t) => t.status !== "completed").length,
        overdueCount: g.todos.filter((t) => t.status !== "completed" && t.dueOn && t.dueOn < today).length,
        todos: g.todos.filter((t) => t.status !== "completed").slice(0, 8).map((t) => ({
          title: t.title, dueOn: t.dueOn, assignee: t.assignee, overdue: !!(t.dueOn && t.dueOn < today),
        })),
      }));
    } else if (tab === "messages") {
      type Msg = { title: string; author: string | null; createdAt: string | null; excerpt: string | null };
      const msgs = data as Msg[];
      projectJson.tabData = msgs.slice(0, 10).map((m) => ({
        title: m.title, author: m.author, date: m.createdAt?.slice(0, 10) ?? null, excerpt: m.excerpt?.slice(0, 120) ?? null,
      }));
    } else if (tab === "schedule") {
      type Entry = { title: string; startsAt: string | null; endsAt: string | null; allDay: boolean };
      const entries = data as Entry[];
      projectJson.tabData = entries.slice(0, 10).map((e) => ({
        title: e.title, startsAt: e.startsAt, endsAt: e.endsAt, allDay: e.allDay,
        isPast: !!(e.startsAt && e.startsAt < today),
      }));
    } else if (tab === "campfire") {
      type CampfireData = { chats?: Array<{ title: string | null }>; lines?: Array<{ author: string | null; createdAt: string | null; content: string | null }> };
      const campfire = data as CampfireData;
      projectJson.tabData = {
        chats: (campfire.chats ?? []).slice(0, 3).map((chat) => ({ title: chat.title })),
        lines: (campfire.lines ?? []).slice(0, 12).map((line) => ({
          author: line.author,
          createdAt: line.createdAt,
          content: line.content?.slice(0, 180) ?? null,
        })),
      };
    } else if (tab === "card_tables") {
      type Table = { name: string; columns: Array<{ name: string; cardsCount: number; cards: Array<{ title: string; dueOn: string | null; assignee: string | null }> }> };
      const tables = data as Table[];
      projectJson.tabData = tables.map((t) => ({
        tableName: t.name,
        columns: (t.columns ?? []).map((c) => ({
          name: c.name, count: c.cardsCount,
          cards: c.cards?.slice(0, 5).map((card) => ({ title: card.title, dueOn: card.dueOn, assignee: card.assignee })) ?? [],
        })),
      }));
    } else if (tab === "files") {
      type FileItem = { title: string; kind: string | null; createdAt: string | null; creator: string | null };
      const files = data as FileItem[];
      projectJson.tabData = files.slice(0, 15).map((f) => ({
        title: f.title, kind: f.kind, createdAt: f.createdAt?.slice(0, 10) ?? null, creator: f.creator,
      }));
    } else if (tab === "people") {
      type Person = { name: string; role: string | null; email: string | null };
      const people = data as Person[];
      projectJson.tabData = people.map((p) => ({ name: p.name, role: p.role, email: p.email }));
    }
  }

  return JSON.stringify(projectJson);
}

function buildOverviewScreenContext(snapshot: PmosProjectsSnapshot | null): string {
  if (!snapshot) return JSON.stringify({ view: "command-center", tab: "overview", status: "loading" });
  const totals = snapshot.totals ?? {};
  return JSON.stringify({
    view: "command-center",
    tab: "overview",
    totals,
    projectCount: totals.projectCount ?? 0,
    topProjects: (snapshot.projects ?? []).slice(0, 6).map((p: PmosProjectCard) => ({
      name: p.name, health: p.health, openTodos: p.openTodos, overdueTodos: p.overdueTodos, dueTodayTodos: p.dueTodayTodos,
    })),
    urgentTodos: (snapshot.urgentTodos ?? []).slice(0, 5).map((t: PmosProjectTodoItem) => ({
      title: t.title, projectName: t.projectName, dueOn: t.dueOn,
    })),
    dueTodayTodos: (snapshot.dueTodayTodos ?? []).slice(0, 5).map((t: PmosProjectTodoItem) => ({
      title: t.title, projectName: t.projectName, dueOn: t.dueOn,
    })),
  });
}

function buildProjectsListScreenContext(snapshot: PmosProjectsSnapshot | null, search: string): string {
  if (!snapshot) return JSON.stringify({ view: "command-center", tab: "projects", status: "loading" });
  const allCards = snapshot.projects ?? [];
  const q = (search || "").trim().toLowerCase();
  const cards = q
    ? allCards.filter((p: PmosProjectCard) =>
        p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q),
      )
    : allCards;
  return JSON.stringify({
    view: "command-center",
    tab: "projects",
    searchQuery: search || null,
    projectCount: cards.length,
    projects: cards.slice(0, 10).map((p: PmosProjectCard) => ({
      name: p.name, health: p.health, openTodos: p.openTodos, overdueTodos: p.overdueTodos,
      assignedTodos: p.assignedTodos, dueTodayTodos: p.dueTodayTodos,
      capabilities: p.dockCapabilities?.slice(0, 5).map((dock) => dock.title ?? dock.name).filter(Boolean),
    })),
  });
}
import {
  applyWorkflowOperationDraft,
  createWorkflowConnection,
  createWorkflow,
  deleteWorkflowConnection,
  deleteWorkflow,
  loadWorkflowConnections,
  loadWorkflows,
  loadWorkflowDetails,
  loadWorkflowPieces,
  loadWorkflowRunDetails,
  loadWorkflowRuns,
  publishWorkflow,
  renameWorkflow,
  retryWorkflowRun,
  setWorkflowStatus,
  triggerWorkflowWebhook,
} from "./controllers/pmos-workflows.ts";
import {
  commitPmosFlowBuilderPlan,
  generatePmosFlowBuilderPlan,
  resetPmosFlowBuilder,
  type PmosFlowGraphEdge,
  type PmosFlowGraphNode,
  type PmosFlowGraphOp,
} from "./controllers/pmos-flow-builder.ts";
import { loadConfig } from "./controllers/config.ts";
import { isPmosFigmaPanelEnabled } from "./controllers/pmos-figma.ts";
import {
  abortPmosLibreChatMessage,
  clearPmosLibreChatStatusPolling,
  isPmosLibreChatEnabled,
  loadPmosLibreChatBootstrap,
  selectPmosLibreChatAgent,
  selectPmosLibreChatAgentModel,
  selectPmosLibreChatConversation,
  sendPmosLibreChatMessage,
  startPmosLibreChatConversation,
  togglePmosLibreChatAgentAccordion,
} from "./controllers/pmos-librechat.ts";

declare global {
  interface Window {
    __OPENCLAW_CONTROL_UI_BASE_PATH__?: string;
    __OPENCLAW_PMOS_SIGNUP_ENABLED__?: boolean;
    __OPENCLAW_PMOS_FIGMA_PANEL_ENABLED__?: boolean;
    __OPENCLAW_PMOS_LIBRECHAT_URL__?: string | null;
  }
}

const injectedAssistantIdentity = resolveInjectedAssistantIdentity();

function resolveOnboardingMode(): boolean {
  if (!window.location.search) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("onboarding");
  if (!raw) {
    return false;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

const PMOS_FIGMA_MESSAGE_TYPES = new Set([
  "pmos:figma-auth-complete",
  "pmos:figma-mcp-auth-complete",
  "pmos:figma-selection-change",
  "pmos:figma-connection-change",
]);

const PMOS_FIGMA_AUTO_SYNC_DEBOUNCE_MS = 500;

@customElement("openclaw-app")
export class OpenClawApp extends LitElement {
  @state() settings: UiSettings = loadSettings();
  @state() password = "";
  @state() tab: Tab = "dashboard";
  @state() onboarding = resolveOnboardingMode();
  @state() onboardingStep: 1 | 2 | 3 = 1;
  @state() notificationsOpen = false;
  @state() pmosAuthLoading = true;
  @state() pmosAuthAuthenticated = false;
  @state() pmosAuthMode: "signin" | "signup" = "signin";
  @state() pmosAuthName = "";
  @state() pmosAuthEmail = "";
  @state() pmosAuthPassword = "";
  @state() pmosAuthError: string | null = null;
  @state() pmosAuthUser: PmosAuthUser | null = null;
  @state() connected = false;
  @state() theme: ThemeMode = this.settings.theme ?? "system";
  @state() themeResolved: ResolvedTheme = "dark";
  @state() hello: GatewayHelloOk | null = null;
  @state() lastError: string | null = null;
  @state() eventLog: EventLogEntry[] = [];
  private eventLogBuffer: EventLogEntry[] = [];
  private toolStreamSyncTimer: number | null = null;
  private sidebarCloseTimer: number | null = null;

  @state() assistantName = injectedAssistantIdentity.name;
  @state() assistantAvatar = injectedAssistantIdentity.avatar;
  @state() assistantAgentId = injectedAssistantIdentity.agentId ?? null;

  @state() sessionKey = this.settings.sessionKey;
  @state() chatLoading = false;
  @state() chatSending = false;
  @state() chatMessage = "";
  @state() chatMessages: unknown[] = [];
  @state() chatToolMessages: unknown[] = [];
  @state() chatStream: string | null = null;
  @state() chatStreamStartedAt: number | null = null;
  @state() chatRunId: string | null = null;
  chatHistoryRecoveryTimer: number | null = null;
  @state() compactionStatus: CompactionStatus | null = null;
  compactionClearTimer: number | null = null;
  @state() chatAvatarUrl: string | null = null;
  @state() chatThinkingLevel: string | null = null;
  @state() chatQueue: ChatQueueItem[] = [];
  @state() chatAttachments: ChatAttachment[] = [];
  @state() chatManualRefreshInFlight = false;
  @state() dashboardNlDraft = "";
  @state() chatTraceLimit = 8;
  @state() chatCreateWorkflowBusy = false;
  @state() chatCreateWorkflowError: string | null = null;
  @state() chatHeaderCollapsed = false;
  // Sidebar state for tool output viewing
  @state() sidebarOpen = false;
  @state() sidebarContent: string | null = null;
  @state() sidebarError: string | null = null;
  @state() splitRatio = this.settings.splitRatio;

  @state() nodesLoading = false;
  @state() nodes: Array<Record<string, unknown>> = [];
  @state() devicesLoading = false;
  @state() devicesError: string | null = null;
  @state() devicesList: DevicePairingList | null = null;
  @state() execApprovalsLoading = false;
  @state() execApprovalsSaving = false;
  @state() execApprovalsDirty = false;
  @state() execApprovalsSnapshot: ExecApprovalsSnapshot | null = null;
  @state() execApprovalsForm: ExecApprovalsFile | null = null;
  @state() execApprovalsSelectedAgent: string | null = null;
  @state() execApprovalsTarget: "gateway" | "node" = "gateway";
  @state() execApprovalsTargetNodeId: string | null = null;
  @state() execApprovalQueue: ExecApprovalRequest[] = [];
  @state() execApprovalBusy = false;
  @state() execApprovalError: string | null = null;
  @state() pendingGatewayUrl: string | null = null;

  @state() configLoading = false;
  @state() configRaw = "{\n}\n";
  @state() configRawOriginal = "";
  @state() configValid: boolean | null = null;
  @state() configIssues: unknown[] = [];
  @state() configSaving = false;
  @state() configApplying = false;
  @state() updateRunning = false;
  @state() applySessionKey = this.settings.lastActiveSessionKey;
  @state() configSnapshot: ConfigSnapshot | null = null;
  @state() configSchema: unknown = null;
  @state() configSchemaVersion: string | null = null;
  @state() configSchemaLoading = false;
  @state() configUiHints: ConfigUiHints = {};
  @state() configForm: Record<string, unknown> | null = null;
  @state() configFormOriginal: Record<string, unknown> | null = null;
  @state() configFormDirty = false;
  @state() configFormMode: "form" | "raw" = "form";
  @state() configSearchQuery = "";
  @state() configActiveSection: string | null = null;
  @state() configActiveSubsection: string | null = null;

  // PMOS connector onboarding (Phase 1)
  @state() pmosConnectorDraftsInitialized = false;
  @state() pmosOpsUrl = "https://flow.wickedlab.io";
  @state() pmosOpsUserEmailDraft = "";
  @state() pmosOpsUserPasswordDraft = "";
  @state() pmosOpsUserHasSavedPassword = false;
  @state() pmosBcgptUrl = "https://bcgpt.wickedlab.io";
    @state() pmosFigmaUrl = "https://fm.wickedlab.io";
  @state() pmosBcgptApiKeyDraft = "";
  @state() pmosIntegrationsSaving = false;
  @state() pmosIntegrationsError: string | null = null;
  @state() pmosConnectorsLoading = false;
  @state() pmosConnectorsStatus: PmosConnectorsStatus | null = null;
  @state() pmosConnectorsError: string | null = null;
  @state() pmosConnectorsLastChecked: number | null = null;
  @state() pmosFigmaContextSyncing = false;
  @state() pmosFigmaContextError: string | null = null;
  @state() pmosFigmaContextSyncedOk = false;
  @state() pmosFigmaLiveAuthVerified = false;
  @state() pmosWorkflowCredentials: Array<{ id: string; name: string; type: string }> | null = null;
  @state() pmosWorkflowCredentialsLoading = false;
  @state() pmosWorkflowCredentialsError: string | null = null;
  @state() pmosTraceEvents: PmosExecutionTraceEvent[] = [];

  // Flow provisioning per workspace
  @state() pmosOpsProvisioning = false;
  @state() pmosOpsProvisioningError: string | null = null;
  @state() pmosOpsProvisioningResult: { projectId?: string; apiKey?: string } | null = null;

  // Manual API-key fallback (when Projects API is license-gated)
  @state() pmosOpsManualApiKeyDraft = "";
  @state() pmosOpsSavingManualKey = false;

  // PMOS model auth quick setup (admin UX)
  @state() pmosModelProvider: PmosModelProvider = "kilo";
  @state() pmosModelId = "minimax/minimax-m2.5:free";
  @state() pmosModelAlias = "";
  @state() pmosModelApiKeyDraft = "";
  @state() pmosModelApiKeyEditable = false;
  @state() pmosModelBaseUrl = "";
  @state() pmosModelApiType = "";
  @state() pmosModelSaving = false;
  @state() pmosModelSavedOk = false;
  @state() pmosModelError: string | null = null;
  @state() pmosModelConfigured = false;
  @state() pmosModelApiKeyStored = false;
  @state() pmosBcgptSavedOk = false;
  @state() pmosBasecampSetupPending = false;
  @state() pmosBasecampSetupOk = false;
  @state() pmosBasecampSetupError: string | null = null;
  @state() pmosByokProviders: PmosModelProvider[] = [];
  @state() pmosModelRows: PmosModelRow[] = [];
  @state() pmosAgentModelAssignments: PmosAgentModelAssignment[] = [];
  @state() pmosModelCatalogLoading = false;
  @state() pmosModelCatalogError: string | null = null;
  @state() pmosWorkspaceConfig: Record<string, unknown> | null = null;
  @state() pmosEffectiveConfig: Record<string, unknown> | null = null;
  @state() pmosModelRefDraft = "";

  // Live workflow-engine connections for the workspace
  @state() pmosRealCredentials: Array<{ id: string; name: string; type: string }> | null = null;
  @state() pmosRealCredentialsLoading = false;
  @state() pmosRealCredentialsError: string | null = null;
  @state() pmosSelectedConnectionId: string | null = null;

  // PMOS identity/admin (Phase 4)
  @state() pmosAdminDraftsInitialized = false;
  @state() pmosAdminLoading = false;
  @state() pmosAdminSaving = false;
  @state() pmosAdminError: string | null = null;
  @state() pmosWorkspaceId = "default";
  @state() pmosWorkspaceName = "PMOS Workspace";
  @state() pmosCurrentUserName = "";
  @state() pmosCurrentUserEmail = "";
  @state() pmosCurrentUserRole: PmosRole = "workspace_admin";
  @state() pmosMembers: PmosMember[] = [];
  @state() pmosMemberDraftName = "";
  @state() pmosMemberDraftEmail = "";
  @state() pmosMemberDraftRole: PmosRole = "member";
  @state() pmosMemberDraftStatus: PmosMemberStatus = "active";
  @state() pmosMemberRemoveConfirm: string | null = null;
  @state() pmosAuditEvents: PmosAuditEvent[] = [];
  @state() pmosWorkspacesList: Array<{ workspaceId: string; ownerEmail: string; ownerName: string; ownerRole: string; createdAtMs: number }> = [];
  @state() pmosWorkspacesLoading = false;
  @state() pmosWorkspacesError: string | null = null;
  @state() pmosGatewayRestarting = false;
  @state() pmosGatewayRestartError: string | null = null;
  @state() pmosPasswordCurrentDraft = "";
  @state() pmosPasswordNewDraft = "";
  @state() pmosPasswordConfirmDraft = "";
  @state() pmosPasswordSaving = false;
  @state() pmosPasswordError: string | null = null;
  @state() pmosPasswordSavedOk = false;
  @state() pmosAdminResetTargetEmail = "";
  @state() pmosAdminResetPasswordDraft = "";
  @state() pmosAdminResetSaving = false;
  @state() pmosAdminResetError: string | null = null;
  @state() pmosAdminResetSavedOk = false;

  @state() pmosWorkspaceResetting = false;
  @state() pmosWorkspaceResetError: string | null = null;
  @state() pmosWorkspaceResetResults: Array<{ workspaceId: string; ok: boolean; error?: string }> | null = null;

  // PMOS unified command center (Phase 6)
  @state() pmosCommandPrompt = "";
  @state() pmosCommandPlanning = false;
  @state() pmosCommandExecuting = false;
  @state() pmosCommandError: string | null = null;
  @state() pmosCommandPlan: PmosCommandPlanStep[] = [];
  @state() pmosCommandHistory: PmosCommandHistoryEntry[] = [];
  @state() pmosCommandPendingApprovals: PmosCommandPendingApproval[] = [];
  @state() pmosProjectsLoading = false;
  @state() pmosProjectsError: string | null = null;
  @state() pmosProjectsSnapshot: PmosProjectsSnapshot | null = null;
  @state() pmosProjectSearch = "";
  @state() pmosProjectViewMode: "cards" | "status-board" | "timeline" = "cards";
  @state() pmosCommandCenterTab: "overview" | "projects" | "timeline" = "overview";
  @state() pmosSelectedProject: PmosProjectCard | null = null;
  @state() pmosProjectDetailTab: PmosProjectDetailTab = "overview";
  @state() pmosProjectSectionData: Record<string, PmosProjectSectionResult> = {};
  @state() pmosSelectedEntityRef: PmosEntityReference | null = null;
  @state() pmosSelectedEntityDetail: PmosEntityDetail | null = null;
  @state() pmosSelectedEntityLoading = false;
  @state() pmosSelectedEntityError: string | null = null;
  @state() pmosProjectActionBusy = false;
  @state() pmosProjectActionError: string | null = null;
  @state() pmosProjectActionMessage: string | null = null;
  @state() pmosTodoDraftTitle = "";
  @state() pmosTodoDraftDescription = "";
  @state() pmosTodoDraftList = "";
  @state() pmosTodoDraftDueOn = "";
  @state() pmosEntityCommentDraft = "";
  @state() pmosScreenContext: string | null = null;
  @state() dashboardTab: "home" | "agents" | "workflows" | "system" = "home";

  // PMOS workflows native embed (Phase 2)
  @state() apPiecesLoading = false;
  @state() apPiecesError: string | null = null;
  @state() apPiecesQuery = "";
  @state() apPieces: import("./controllers/pmos-workflows.ts").WorkflowPieceSummary[] = [];
  @state() apPieceSelectedName: string | null = null;
  @state() apPieceDetailsLoading = false;
  @state() apPieceDetailsError: string | null = null;
  @state() apPieceDetails: unknown | null = null;

  @state() apConnectionsLoading = false;
  @state() apConnectionsError: string | null = null;
  @state() apConnections: import("./controllers/pmos-workflows.ts").WorkflowConnectionSummary[] =
    [];
  @state() apConnectionsCursor: string | null = null;
  @state() apConnectionsHasNext = false;
  @state() apConnectionCreateSaving = false;
  @state() apConnectionCreateError: string | null = null;
  @state() apConnectionCreatePieceName = "";
  @state() apConnectionCreateDisplayName = "";
  @state() apConnectionCreateType: "secret_text" | "basic_auth" | "no_auth" = "secret_text";
  @state() apConnectionCreateSecretText = "";
  @state() apConnectionCreateBasicUser = "";
  @state() apConnectionCreateBasicPass = "";

  @state() apFlowsLoading = false;
  @state() apFlowsError: string | null = null;
  @state() apFlowsQuery = "";
  @state() apFlows: import("./controllers/pmos-workflows.ts").WorkflowSummary[] = [];
  @state() apFlowsCursor: string | null = null;
  @state() apFlowsHasNext = false;
  @state() apFlowCreateName = "";
  @state() apFlowCreateSaving = false;
  @state() apFlowCreateError: string | null = null;
  @state() apFlowSelectedId: string | null = null;
  @state() workflowEmbedVersion = 0;
  @state() flowConnectionsEmbedVersion = 0;
  @state() pmosFigmaEmbedVersion = 0;
  @state() libreChatEmbedVersion = 0;
  @state() libreChatLoading = false;
  @state() libreChatError: string | null = null;
  @state() libreChatUrl: string | null = null;
  @state() libreChatAutologinConfigured = false;
  @state() libreChatAvailableModels: string[] = [];
  @state() libreChatAgents: import("./controllers/pmos-librechat.ts").PmosLibreChatAgent[] = [];
  @state() libreChatConversations: import("./controllers/pmos-librechat.ts").PmosLibreChatConversation[] = [];
  @state() libreChatMessages: import("./controllers/pmos-librechat.ts").PmosLibreChatMessage[] = [];
  @state() libreChatSelectedAgentId: string | null = null;
  @state() libreChatSelectedConversationId: string | null = null;
  @state() libreChatDraft = "";
  @state() libreChatSending = false;
  @state() libreChatParentMessageId: string | null = null;
  @state() libreChatStreamingMessage: import("./controllers/pmos-librechat.ts").PmosLibreChatMessage | null = null;
  @state() libreChatOpenAgentIds: string[] = [];
  libreChatStatusPollTimer: number | null = null;
  @state() apFlowDetailsLoading = false;
  @state() apFlowDetailsError: string | null = null;
  @state() apFlowDetails: unknown | null = null;
  @state() apFlowRenameDraft = "";
  @state() apFlowOperationDraft = "";
  @state() apFlowTriggerPayloadDraft = "{\n  \n}\n";
  @state() apFlowMutating = false;
  @state() apFlowMutateError: string | null = null;
  @state() apFlowTemplateDeployedOk = false;
  @state() automationsPanelOpen = true;
  @state() automationsPanelTab: "workflows" | "runs" = "workflows";
  @state() automationsChatOpen = false;
  @state() automationsLeftPanelRatio = 0.28;
  @state() automationsCenterSplitRatio = 0.72;
  @state() workflowChatDraft = "";
  @state() workflowChatMessages: Array<{ role: "user" | "assistant"; content: string }> = [];
  @state() workflowChatSending = false;
  @state() workflowChatStream: string | null = null;
  @state() workflowChatStreamStartedAt: number | null = null;
  @state() workflowChatSteps: string[] = [];
  @state() workflowChatPendingWorkflow: { name: string; nodes: unknown[]; connections: Record<string, unknown> } | null = null;

  // PMOS AI flow builder stream (Phase 5)
  @state() pmosFlowBuilderPrompt = "";
  @state() pmosFlowBuilderGenerating = false;
  @state() pmosFlowBuilderCommitting = false;
  @state() pmosFlowBuilderError: string | null = null;
  @state() pmosFlowBuilderFlowName = "";
  @state() pmosFlowBuilderNodes: PmosFlowGraphNode[] = [];
  @state() pmosFlowBuilderEdges: PmosFlowGraphEdge[] = [];
  @state() pmosFlowBuilderOps: PmosFlowGraphOp[] = [];
  @state() pmosFlowBuilderOpIndex = 0;
  @state() pmosFlowBuilderLastCommittedFlowId: string | null = null;

  @state() apRunsLoading = false;
  @state() apRunsError: string | null = null;
  @state() apRuns: import("./controllers/pmos-workflows.ts").WorkflowRunSummary[] = [];
  @state() apRunsCursor: string | null = null;
  @state() apRunsHasNext = false;
  @state() apRunSelectedId: string | null = null;
  @state() apRunDetailsLoading = false;
  @state() apRunDetailsError: string | null = null;
  @state() apRunDetails: unknown | null = null;
  @state() apRunRetrying = false;
  @state() apRunRetryError: string | null = null;

  @state() channelsLoading = false;
  @state() channelsSnapshot: ChannelsStatusSnapshot | null = null;
  @state() channelsError: string | null = null;
  @state() channelsLastSuccess: number | null = null;
  @state() whatsappLoginMessage: string | null = null;
  @state() whatsappLoginQrDataUrl: string | null = null;
  @state() whatsappLoginConnected: boolean | null = null;
  @state() whatsappBusy = false;
  @state() nostrProfileFormState: NostrProfileFormState | null = null;
  @state() nostrProfileAccountId: string | null = null;

  @state() presenceLoading = false;
  @state() presenceEntries: PresenceEntry[] = [];
  @state() presenceError: string | null = null;
  @state() presenceStatus: string | null = null;

  @state() agentsLoading = false;
  @state() agentsList: AgentsListResult | null = null;
  @state() agentsError: string | null = null;
  @state() agentsSelectedId: string | null = null;
  @state() agentsPanel: "overview" | "files" | "tools" | "skills" | "channels" | "cron" =
    "overview";
  @state() agentFilesLoading = false;
  @state() agentFilesError: string | null = null;
  @state() agentFilesList: AgentsFilesListResult | null = null;
  @state() agentFileContents: Record<string, string> = {};
  @state() agentFileDrafts: Record<string, string> = {};
  @state() agentFileActive: string | null = null;
  @state() agentFileSaving = false;
  @state() agentIdentityLoading = false;
  @state() agentIdentityError: string | null = null;
  @state() agentIdentityById: Record<string, AgentIdentityResult> = {};
  @state() agentSkillsLoading = false;
  @state() agentSkillsError: string | null = null;
  @state() agentSkillsReport: SkillStatusReport | null = null;
  @state() agentSkillsAgentId: string | null = null;
  // Create Agent Modal state
  @state() createModalOpen = false;
  @state() createModalMode: "create" | "edit" = "create";
  @state() createModalEditAgentId: string | null = null;
  @state() createModalStep: 0 | 1 | 2 | 3 = 0;
  @state() createModalLoading = false;
  @state() createModalError: string | null = null;
  @state() createModalFormData: import("./views/agents.js").CreateAgentFormData = {
    name: "",
    id: "",
    purpose: "",
    workspace: DEFAULT_AGENT_WORKSPACE_PATH,
    emoji: "🤖",
    theme: "",
    mode: "autonomous",
    model: "",
    skills: [],
    personality: "professional",
    autonomousTasks: [],
    archetypeId: "",
    soulContent: "",
  };
  @state() availableModels: string[] = [];
  @state() availableSkills: string[] = [];
  @state() catalogDivision = "all";
  @state() catalogSearch = "";
  @state() catalogPreviewArchetypeId: string | null = null;
  @state() catalogPreviewSoulContent = "";
  @state() catalogPreviewLoading = false;
  @state() catalogPreviewError: string | null = null;
  @state() agentActivityById: Record<string, import("./views/agents.js").AgentActivitySummary> = {};

  @state() sessionsLoading = false;
  @state() sessionsResult: SessionsListResult | null = null;
  @state() sessionsError: string | null = null;
  @state() sessionsFilterActive = "";
  @state() sessionsFilterLimit = "120";
  @state() sessionsIncludeGlobal = true;
  @state() sessionsIncludeUnknown = false;

  @state() usageLoading = false;
  @state() usageResult: import("./types.js").SessionsUsageResult | null = null;
  @state() usageCostSummary: import("./types.js").CostUsageSummary | null = null;
  @state() usageError: string | null = null;
  @state() usageStartDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageEndDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageSelectedSessions: string[] = [];
  @state() usageSelectedDays: string[] = [];
  @state() usageSelectedHours: number[] = [];
  @state() usageChartMode: "tokens" | "cost" = "tokens";
  @state() usageDailyChartMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeriesMode: "cumulative" | "per-turn" = "per-turn";
  @state() usageTimeSeriesBreakdownMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeries: import("./types.js").SessionUsageTimeSeries | null = null;
  @state() usageTimeSeriesLoading = false;
  @state() usageSessionLogs: import("./views/usage.js").SessionLogEntry[] | null = null;
  @state() usageSessionLogsLoading = false;
  @state() usageSessionLogsExpanded = false;
  // Applied query (used to filter the already-loaded sessions list client-side).
  @state() usageQuery = "";
  // Draft query text (updates immediately as the user types; applied via debounce or "Search").
  @state() usageQueryDraft = "";
  @state() usageSessionSort: "tokens" | "cost" | "recent" | "messages" | "errors" = "recent";
  @state() usageSessionSortDir: "desc" | "asc" = "desc";
  @state() usageRecentSessions: string[] = [];
  @state() usageTimeZone: "local" | "utc" = "local";
  @state() usageContextExpanded = false;
  @state() usageHeaderPinned = false;
  @state() usageSessionsTab: "all" | "recent" = "all";
  @state() usageVisibleColumns: string[] = [
    "channel",
    "agent",
    "provider",
    "model",
    "messages",
    "tools",
    "errors",
    "duration",
  ];
  @state() usageLogFilterRoles: import("./views/usage.js").SessionLogRole[] = [];
  @state() usageLogFilterTools: string[] = [];
  @state() usageLogFilterHasTools = false;
  @state() usageLogFilterQuery = "";

  // Non-reactive (don't trigger renders just for timer bookkeeping).
  usageQueryDebounceTimer: number | null = null;

  @state() cronLoading = false;
  @state() cronJobs: CronJob[] = [];
  @state() cronStatus: CronStatus | null = null;
  @state() cronError: string | null = null;
  @state() cronForm: CronFormState = { ...DEFAULT_CRON_FORM };
  @state() cronRunsJobId: string | null = null;
  @state() cronRuns: CronRunLogEntry[] = [];
  @state() cronBusy = false;

  @state() skillsLoading = false;
  @state() skillsReport: SkillStatusReport | null = null;
  @state() skillsError: string | null = null;
  @state() skillsFilter = "";
  @state() skillEdits: Record<string, string> = {};
  @state() skillsBusyKey: string | null = null;
  @state() skillMessages: Record<string, SkillMessage> = {};

  @state() debugLoading = false;
  @state() debugStatus: StatusSummary | null = null;
  @state() debugHealth: HealthSnapshot | null = null;
  @state() debugModels: unknown[] = [];
  @state() debugHeartbeat: unknown = null;
  @state() debugCallMethod = "";
  @state() debugCallParams = "{}";
  @state() debugCallResult: string | null = null;
  @state() debugCallError: string | null = null;

  @state() logsLoading = false;
  @state() logsError: string | null = null;
  @state() logsFile: string | null = null;
  @state() logsEntries: LogEntry[] = [];
  @state() logsFilterText = "";
  @state() logsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() logsAutoFollow = true;
  @state() logsTruncated = false;
  @state() logsCursor: number | null = null;
  @state() logsLastFetchAt: number | null = null;
  @state() logsLimit = 500;
  @state() logsMaxBytes = 250_000;
  @state() logsAtBottom = true;

  client: GatewayBrowserClient | null = null;
  private chatScrollFrame: number | null = null;
  private chatScrollTimeout: number | null = null;
  private chatHasAutoScrolled = false;
  private chatUserNearBottom = true;
  @state() chatNewMessagesBelow = false;
  private nodesPollInterval: number | null = null;
  dashboardPollInterval: number | null = null;
  private logsPollInterval: number | null = null;
  private debugPollInterval: number | null = null;
  private logsScrollFrame: number | null = null;
  private toolStreamById = new Map<string, ToolStreamEntry>();
  private toolStreamOrder: string[] = [];
  refreshSessionsAfterChat = new Set<string>();
  basePath = "";
  private popStateHandler = () =>
    onPopStateInternal(this as unknown as Parameters<typeof onPopStateInternal>[0]);
  private themeMedia: MediaQueryList | null = null;
  private themeMediaHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private topbarObserver: ResizeObserver | null = null;
  private pmosFigmaAutoSyncTimer: number | null = null;
  private pmosFigmaAuthPopup: Window | null = null;
  private pmosFigmaAuthPopupPollTimer: number | null = null;
  private pmosFigmaAuthPopupStartedAt: number | null = null;
  private async persistPmosFigmaContext(
    figmaBaseUrl: string,
    payload: {
      user?: { handle?: string | null; email?: string | null };
      fmMcpUrl?: string | null;
      fmMcpApiToken?: string | null;
      auth?: {
        personalAccessToken?: string | null;
        hasPersonalAccessToken?: boolean | null;
        source?: string | null;
        mcpServerUrl?: string | null;
        fmMcpUrl?: string | null;
        fmMcpApiToken?: string | null;
        updatedAt?: string | null;
      } | null;
      activeConnection?: {
        id?: string | number | null;
        connection_name?: string | null;
        figma_team_id?: string | null;
        last_synced_at?: string | number | null;
        last_pat_validated_at?: string | number | null;
        updated_at?: string | null;
      } | null;
      selection?: {
        selectedFileUrl?: string | null;
        selectedFileId?: string | null;
        selectedFileName?: string | null;
        updatedAt?: string | null;
      } | null;
      connectionsCount?: number;
    },
  ) {
    if (!this.client) {
      return;
    }
    const fmHasValidatedPat =
      payload.auth?.hasPersonalAccessToken === true ||
      Boolean(payload.auth?.personalAccessToken) ||
      (payload.activeConnection?.last_pat_validated_at !== null &&
        payload.activeConnection?.last_pat_validated_at !== undefined);

    await this.client.request("pmos.connectors.workspace.set", {
      connectors: {
        figma: {
          url: figmaBaseUrl,
          auth: {
            personalAccessToken: payload.auth?.personalAccessToken ?? null,
            hasPersonalAccessToken: fmHasValidatedPat,
            source:
              payload.auth?.source ??
              (fmHasValidatedPat ? "fm-team-pat" : null),
            mcpServerUrl: payload.auth?.mcpServerUrl ?? null,
            fmMcpUrl: payload.fmMcpUrl ?? payload.auth?.fmMcpUrl ?? null,
            fmMcpApiToken: payload.fmMcpApiToken ?? payload.auth?.fmMcpApiToken ?? null,
            updatedAt:
              payload.auth?.updatedAt ??
              payload.activeConnection?.updated_at ??
              new Date().toISOString(),
          },
          identity: {
            connected: Boolean(payload.user?.handle || payload.user?.email),
            handle: payload.user?.handle ?? null,
            email: payload.user?.email ?? null,
            hasPersonalAccessToken: fmHasValidatedPat,
            activeConnectionId:
              payload.activeConnection?.id === null || payload.activeConnection?.id === undefined
                ? null
                : String(payload.activeConnection.id),
            activeConnectionName: payload.activeConnection?.connection_name ?? null,
            activeTeamId: payload.activeConnection?.figma_team_id ?? null,
            totalConnections:
              typeof payload.connectionsCount === "number" ? payload.connectionsCount : undefined,
            lastSyncedAt: payload.activeConnection?.last_synced_at ?? null,
            selectedFileUrl: payload.selection?.selectedFileUrl ?? null,
            selectedFileId: payload.selection?.selectedFileId ?? null,
            selectedFileName: payload.selection?.selectedFileName ?? null,
            updatedAt: payload.selection?.updatedAt ?? new Date().toISOString(),
          },
        },
      },
    });

    await loadPmosConnectorsStatus(this);
    this.pmosConnectorDraftsInitialized = false;
    hydratePmosConnectorDraftsFromConfig(this);
    try {
      await this.client.request("pmos.context.workspace.refresh", {});
    } catch {
      // Best-effort context refresh only.
    }
  }
  private queuePmosFigmaAutoSync() {
    if (this.pmosFigmaAutoSyncTimer !== null) {
      window.clearTimeout(this.pmosFigmaAutoSyncTimer);
    }
    this.pmosFigmaAutoSyncTimer = window.setTimeout(() => {
      this.pmosFigmaAutoSyncTimer = null;
      void this.handlePmosFigmaSyncContext({ auto: true });
    }, PMOS_FIGMA_AUTO_SYNC_DEBOUNCE_MS);
  }

  private clearPmosFigmaAuthPopupMonitor() {
    if (this.pmosFigmaAuthPopupPollTimer !== null) {
      window.clearInterval(this.pmosFigmaAuthPopupPollTimer);
      this.pmosFigmaAuthPopupPollTimer = null;
    }
    this.pmosFigmaAuthPopup = null;
    this.pmosFigmaAuthPopupStartedAt = null;
  }

  private beginPmosFigmaAuthPopupMonitor(popup: Window | null) {
    this.clearPmosFigmaAuthPopupMonitor();
    if (!popup) {
      return;
    }
    this.pmosFigmaAuthPopup = popup;
    this.pmosFigmaAuthPopupStartedAt = Date.now();
    let closedChecks = 0;

    const tick = async () => {
      const activePopup = this.pmosFigmaAuthPopup;
      const popupClosed = !activePopup || activePopup.closed;
      const startedAt = this.pmosFigmaAuthPopupStartedAt ?? Date.now();
      try {
        await this.verifyFigmaLiveAuth({ autoSync: true });
      } catch {
        // Best-effort polling only.
      }
      if (this.pmosFigmaLiveAuthVerified) {
        this.clearPmosFigmaAuthPopupMonitor();
        return;
      }
      if (popupClosed) {
        closedChecks += 1;
        if (closedChecks >= 3) {
          this.clearPmosFigmaAuthPopupMonitor();
          if (!this.pmosFigmaLiveAuthVerified) {
            this.pmosFigmaContextError =
              "Figma sign-in finished, but PMOS did not detect a synced FM session yet. Try Sync Now once, or reopen the popup.";
          }
        }
        return;
      }
      if (Date.now() - startedAt > 120_000) {
        this.clearPmosFigmaAuthPopupMonitor();
        if (!this.pmosFigmaLiveAuthVerified) {
          this.pmosFigmaContextError =
            "Figma sign-in timed out before PMOS could verify the FM session. Reopen the popup and try again.";
        }
      }
    };

    this.pmosFigmaAuthPopupPollTimer = window.setInterval(() => {
      void tick();
    }, 1500);
    void tick();
  }

  handlePmosOpenFigmaAuthPopup() {
    const figmaBaseUrl =
      String(this.pmosFigmaUrl || this.pmosConnectorsStatus?.figma?.url || "https://fm.wickedlab.io")
        .trim()
        .replace(/\/+$/, "") || "https://fm.wickedlab.io";
    const authUrl = `${figmaBaseUrl}/auth/figma?pmosEmbed=1&pmosParentOrigin=${encodeURIComponent(
      typeof window !== "undefined" ? window.location.origin : "",
    )}`;
    this.pmosFigmaContextError = null;
    this.pmosFigmaContextSyncedOk = false;
    const popup = window.open(
      authUrl,
      "pmos-figma-auth",
      "popup=yes,width=720,height=900,resizable=yes,scrollbars=yes",
    );
    if (popup) {
      popup.focus();
      this.beginPmosFigmaAuthPopupMonitor(popup);
      return;
    }
    this.clearPmosFigmaAuthPopupMonitor();
    window.location.href = authUrl;
  }

  private readonly pmosFigmaAuthMessageHandler = (event: MessageEvent) => {
    let data: unknown = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return;
    }
    const messageType = (data as { type?: unknown }).type;
    if (typeof messageType !== "string" || !PMOS_FIGMA_MESSAGE_TYPES.has(messageType)) {
      return;
    }
    if (messageType === "pmos:figma-mcp-auth-complete") {
      if (event.origin !== window.location.origin) {
        return;
      }
    } else {
      const figmaBaseUrl = String(
        this.pmosFigmaUrl || this.pmosConnectorsStatus?.figma?.url || "https://fm.wickedlab.io",
      )
        .trim()
        .replace(/\/+$/, "");
      let expectedOrigin: string | null = null;
      try {
        expectedOrigin = new URL(figmaBaseUrl).origin;
      } catch {
        expectedOrigin = null;
      }
      if (!expectedOrigin || event.origin !== expectedOrigin) {
        return;
      }
    }
    this.pmosFigmaContextError = null;
    this.pmosFigmaContextSyncedOk = false;
    if (messageType === "pmos:figma-auth-complete") {
      this.pmosFigmaEmbedVersion = (this.pmosFigmaEmbedVersion ?? 0) + 1;
      // Auth just completed — verify live auth and auto-sync immediately
      void this.verifyFigmaLiveAuth();
      this.beginPmosFigmaAuthPopupMonitor(this.pmosFigmaAuthPopup);
    }
    if (messageType === "pmos:figma-mcp-auth-complete") {
      void loadPmosConnectorsStatus(this);
    }
    void this.handlePmosRefreshConnectors();
    this.queuePmosFigmaAutoSync();
  };

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("message", this.pmosFigmaAuthMessageHandler);
    handleConnected(this as unknown as Parameters<typeof handleConnected>[0]);
    void this.handlePmosAuthBootstrap();
  }

  protected firstUpdated() {
    handleFirstUpdated(this as unknown as Parameters<typeof handleFirstUpdated>[0]);
  }

  disconnectedCallback() {
    window.removeEventListener("message", this.pmosFigmaAuthMessageHandler);
    if (this.pmosFigmaAutoSyncTimer !== null) {
      window.clearTimeout(this.pmosFigmaAutoSyncTimer);
      this.pmosFigmaAutoSyncTimer = null;
    }
    clearPmosLibreChatStatusPolling(this);
    this.clearPmosFigmaAuthPopupMonitor();
    handleDisconnected(this as unknown as Parameters<typeof handleDisconnected>[0]);
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    handleUpdated(this as unknown as Parameters<typeof handleUpdated>[0], changed);
  }

  connect() {
    connectGatewayInternal(this as unknown as Parameters<typeof connectGatewayInternal>[0]);
  }

  async handlePmosAuthBootstrap() {
    await loadPmosAuthSession(this);
    if (this.pmosAuthAuthenticated) {
      this.onboarding = false;
      this.connect();
      if (isPmosLibreChatEnabled()) {
        await this.handleLibreChatLoad();
      }
    }
  }

  async handlePmosAuthSubmit() {
    const ok =
      this.pmosAuthMode === "signup" && isPmosSignupEnabled()
        ? await signupPmosAuth(this)
        : await loginPmosAuth(this);
    if (!ok) {
      return;
    }
    this.lastError = null;
    // Starter experience is now auto-provisioned server-side (shared Ollama + starter agent),
    // so skip onboarding and drop users straight into dashboard.
    this.onboarding = false;
    this.setTab("dashboard");
    this.connect();
    if (isPmosLibreChatEnabled()) {
      await this.handleLibreChatLoad();
    }
  }

  async handlePmosAuthLogout() {
    clearPmosLibreChatStatusPolling(this);
    await logoutPmosAuth(this);
    this.libreChatError = null;
    this.libreChatAvailableModels = [];
    this.libreChatAgents = [];
    this.libreChatConversations = [];
    this.libreChatMessages = [];
    this.libreChatSelectedAgentId = null;
    this.libreChatSelectedConversationId = null;
    this.libreChatDraft = "";
    this.libreChatSending = false;
    this.libreChatParentMessageId = null;
    this.libreChatStreamingMessage = null;
    this.libreChatOpenAgentIds = [];
    this.client?.stop();
    this.client = null;
    this.connected = false;
    this.hello = null;
    this.lastError = null;
    this.execApprovalQueue = [];
    this.execApprovalError = null;
  }

  async handleLibreChatLoad() {
    await loadPmosLibreChatBootstrap(this, { preserveSelection: true });
    const conversationId = this.libreChatSelectedConversationId?.trim() ?? "";
    const loadedConversationId =
      typeof this.libreChatMessages[0]?.conversationId === "string"
        ? this.libreChatMessages[0].conversationId
        : "";
    if (
      conversationId &&
      (!loadedConversationId || loadedConversationId !== conversationId) &&
      !this.libreChatSending
    ) {
      await selectPmosLibreChatConversation(this, conversationId);
    }
  }

  handleLibreChatSelectAgent(agentId: string) {
    selectPmosLibreChatAgent(this, agentId);
  }

  handleLibreChatSelectModel(agentId: string, model: string) {
    selectPmosLibreChatAgentModel(this, agentId, model);
  }

  handleLibreChatToggleAgent(agentId: string) {
    togglePmosLibreChatAgentAccordion(this, agentId);
  }

  handleLibreChatStartConversation(agentId: string) {
    startPmosLibreChatConversation(this, agentId);
  }

  async handleLibreChatSelectConversation(conversationId: string) {
    await selectPmosLibreChatConversation(this, conversationId);
  }

  async handleLibreChatSend() {
    await sendPmosLibreChatMessage(this);
  }

  async handleLibreChatAbort() {
    await abortPmosLibreChatMessage(this);
  }

  handleChatScroll(event: Event) {
    handleChatScrollInternal(
      this as unknown as Parameters<typeof handleChatScrollInternal>[0],
      event,
    );
  }

  handleLogsScroll(event: Event) {
    handleLogsScrollInternal(
      this as unknown as Parameters<typeof handleLogsScrollInternal>[0],
      event,
    );
  }

  exportLogs(lines: string[], label: string) {
    exportLogsInternal(lines, label);
  }

  resetToolStream() {
    resetToolStreamInternal(this as unknown as Parameters<typeof resetToolStreamInternal>[0]);
  }

  resetChatScroll() {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
  }

  scrollToBottom(opts?: { smooth?: boolean }) {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
    scheduleChatScrollInternal(
      this as unknown as Parameters<typeof scheduleChatScrollInternal>[0],
      true,
      Boolean(opts?.smooth),
    );
  }

  async loadAssistantIdentity() {
    await loadAssistantIdentityInternal(this);
  }

  applySettings(next: UiSettings) {
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], next);
  }

  setTab(next: Tab) {
    if (next === "figma" && !isPmosFigmaPanelEnabled()) {
      next = "dashboard";
    }
    if (next === "librechat" && !isPmosLibreChatEnabled()) {
      next = "dashboard";
    }
    setTabInternal(this as unknown as Parameters<typeof setTabInternal>[0], next);
    // Auto-load workspaces list when super_admin opens admin tab
    if (next === "admin" && this.pmosAuthUser?.role === "super_admin" && this.pmosWorkspacesList.length === 0 && !this.pmosWorkspacesLoading) {
      void this._loadWorkspacesList();
    }
    // When switching to Figma tab, verify live auth before showing iframe
    if (next === "figma") {
      void this.verifyFigmaLiveAuth({ autoSync: true });
    }
    // When switching to connections tab, load real credentials
    if (next === "connections") {
      void this.handleLoadRealCredentials();
    }
  }

  /** Quick auth check against FM API — clears stale connected state on 401 and auto-syncs usable context. */
  private async verifyFigmaLiveAuth(opts?: { autoSync?: boolean }) {
    const figmaBaseUrl = String(this.pmosFigmaUrl ?? "").trim().replace(/\/+$/, "") || "https://fm.wickedlab.io";
    try {
      const res = await fetch(`${figmaBaseUrl}/api/pmos/context`, {
        credentials: "include",
        headers: { accept: "application/json" },
      });
      if (res.status === 401) {
        this.pmosFigmaLiveAuthVerified = false;
        // Clear stale connected state so sign-in CTA shows
        if (this.client) {
          await this.client.request("pmos.connectors.workspace.set", {
            connectors: {
              figma: {
                url: figmaBaseUrl,
                identity: { connected: false },
              },
            },
          }).catch(() => {});
        }
        await loadPmosConnectorsStatus(this);
      } else if (res.ok) {
        const payload = (await res.json()) as {
          user?: { handle?: string | null; email?: string | null };
          auth?: {
            personalAccessToken?: string | null;
            hasPersonalAccessToken?: boolean | null;
            source?: string | null;
            mcpServerUrl?: string | null;
            fmMcpUrl?: string | null;
            fmMcpApiToken?: string | null;
            updatedAt?: string | null;
          } | null;
          activeConnection?: {
            id?: string | number | null;
            connection_name?: string | null;
            figma_team_id?: string | null;
            last_synced_at?: string | number | null;
            last_pat_validated_at?: string | number | null;
            updated_at?: string | null;
          } | null;
          selection?: {
            selectedFileUrl?: string | null;
            selectedFileId?: string | null;
            selectedFileName?: string | null;
            updatedAt?: string | null;
          } | null;
          connectionsCount?: number;
        };
        this.pmosFigmaLiveAuthVerified = true;
        if (opts?.autoSync !== false && this.client && this.connected) {
          await this.persistPmosFigmaContext(figmaBaseUrl, payload);
          this.pmosFigmaContextSyncedOk = true;
          setTimeout(() => {
            this.pmosFigmaContextSyncedOk = false;
          }, 2500);
        }
      }
    } catch {
      // Network error — keep existing state
    }
  }

  async _loadWorkspacesList() {
    this.pmosWorkspacesLoading = true;
    this.pmosWorkspacesError = null;
    try {
      const res = await this.client!.request<{ workspaces: Array<{ workspaceId: string; ownerEmail: string; ownerName: string; ownerRole: string; createdAtMs: number }> }>("pmos.workspaces.list", {});
      this.pmosWorkspacesList = res.workspaces ?? [];
    } catch (err) {
      this.pmosWorkspacesError = String(err);
    } finally {
      this.pmosWorkspacesLoading = false;
    }
  }

  setTheme(next: ThemeMode, context?: Parameters<typeof setThemeInternal>[2]) {
    setThemeInternal(this as unknown as Parameters<typeof setThemeInternal>[0], next, context);
  }

  async loadOverview() {
    await loadOverviewInternal(this as unknown as Parameters<typeof loadOverviewInternal>[0]);
  }

  async loadCron() {
    await loadCronInternal(this as unknown as Parameters<typeof loadCronInternal>[0]);
  }

  async handleAbortChat() {
    await handleAbortChatInternal(this as unknown as Parameters<typeof handleAbortChatInternal>[0]);
  }

  async handlePmosRefreshConnectors() {
    await loadPmosConnectorsStatus(this);
    if (!this.pmosConnectorDraftsInitialized) {
      hydratePmosConnectorDraftsFromConfig(this);
    }
    await loadPmosModelWorkspaceState(this);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
    await loadPmosWorkflowCredentials(this);

    // Refresh workspace-scoped Wicked Ops connectors (if any)
    try {
      if (this.client && this.connected) {
        const ws = await this.client.request<{ workspaceId: string; connectors: Record<string, any> }>(
          "pmos.connectors.workspace.get",
          {},
        );
        const ops = ws?.connectors?.ops ?? null;
        const opsUser = ops && typeof ops === "object" ? (ops as Record<string, unknown>).user : null;
        const opsUserObj =
          opsUser && typeof opsUser === "object" && !Array.isArray(opsUser)
            ? (opsUser as Record<string, unknown>)
            : null;
        this.pmosOpsUserEmailDraft =
          opsUserObj && typeof opsUserObj.email === "string" ? opsUserObj.email : "";
        this.pmosOpsUserHasSavedPassword = Boolean(
          opsUserObj?.hasPassword ||
            (typeof opsUserObj?.password === "string" && opsUserObj.password.length > 0),
        );
        this.pmosOpsUserPasswordDraft = "";
        if (ops && typeof ops === "object" && ops.apiKey) {
          const key = String(ops.apiKey);
          this.pmosOpsProvisioningResult = { projectId: ops.projectId ?? undefined, apiKey: key };
          this.pmosOpsManualApiKeyDraft = key;
          this.pmosOpsProvisioningError = null;
        } else {
          this.pmosOpsProvisioningResult = null;
          this.pmosOpsManualApiKeyDraft = "";
        }
      }
    } catch (err) {
      // ignore workspace-read failures (UI remains usable)
      this.pmosOpsProvisioningResult = null;
    }

    if (this.client && this.connected && !this.pmosFigmaContextSyncing) {
      void this.verifyFigmaLiveAuth({ autoSync: true });
    }
  }

  async handlePmosPrepareFigmaMcp() {
    if (!this.client || !this.connected) {
      this.pmosConnectorsError = "Connect to the gateway first.";
      return;
    }
    this.pmosConnectorsError = null;
    try {
      await this.client.request("pmos.figma.mcp.prepare", {});
      await loadPmosConnectorsStatus(this);
      await this.verifyFigmaLiveAuth({ autoSync: true });
    } catch (err) {
      this.pmosConnectorsError = String(err);
    }
  }

  handlePmosTraceClear() {
    this.pmosTraceEvents = [];
  }

  async handlePmosAdminLoad() {
    await loadPmosAdminState(this);
    hydratePmosAdminFromConfig(this);
  }

  async handleGatewayRestart() {
    await restartGateway(this);
  }

  async handleResetAllWorkspaces() {
    await resetAllWorkspaces(this);
    // Refresh workspace list after reset
    if (this.pmosWorkspaceResetError === null) {
      await this._loadWorkspacesList();
    }
  }

  async handlePmosAdminSave(opts?: { action?: string; target?: string; detail?: string }) {
    await savePmosAdminState(this, opts);
    await loadPmosAdminState(this);
  }

  async handlePmosPasswordChange() {
    this.pmosPasswordError = null;
    this.pmosPasswordSavedOk = false;
    const currentPassword = this.pmosPasswordCurrentDraft;
    const newPassword = this.pmosPasswordNewDraft;
    const confirmPassword = this.pmosPasswordConfirmDraft;
    if (!currentPassword.trim()) {
      this.pmosPasswordError = "Current password is required.";
      return;
    }
    if (newPassword.length < 8) {
      this.pmosPasswordError = "New password must be at least 8 characters.";
      return;
    }
    if (newPassword !== confirmPassword) {
      this.pmosPasswordError = "New password and confirmation must match.";
      return;
    }
    this.pmosPasswordSaving = true;
    try {
      const result = await changePmosAuthPassword(this, {
        currentPassword,
        newPassword,
      });
      if (!result.ok) {
        this.pmosPasswordError = result.error || "Failed to change password.";
        return;
      }
      this.pmosPasswordCurrentDraft = "";
      this.pmosPasswordNewDraft = "";
      this.pmosPasswordConfirmDraft = "";
      this.pmosPasswordSavedOk = true;
      setTimeout(() => {
        this.pmosPasswordSavedOk = false;
      }, 3000);
    } finally {
      this.pmosPasswordSaving = false;
    }
  }

  async handlePmosAdminResetUserPassword() {
    this.pmosAdminResetError = null;
    this.pmosAdminResetSavedOk = false;
    if (
      this.pmosAuthUser?.role !== "super_admin" &&
      this.pmosAuthUser?.role !== "workspace_admin"
    ) {
      this.pmosAdminResetError = "workspace_admin or super_admin role required.";
      return;
    }
    const email = this.pmosAdminResetTargetEmail.trim().toLowerCase();
    const newPassword = this.pmosAdminResetPasswordDraft;
    if (!email) {
      this.pmosAdminResetError = "Target user email is required.";
      return;
    }
    if (newPassword.length < 8) {
      this.pmosAdminResetError = "New password must be at least 8 characters.";
      return;
    }
    this.pmosAdminResetSaving = true;
    try {
      const result = await adminResetPmosAuthPassword(this, { email, newPassword });
      if (!result.ok) {
        this.pmosAdminResetError = result.error || "Failed to reset user password.";
        return;
      }
      this.pmosAdminResetPasswordDraft = "";
      this.pmosAdminResetSavedOk = true;
      setTimeout(() => {
        this.pmosAdminResetSavedOk = false;
      }, 3000);
    } finally {
      this.pmosAdminResetSaving = false;
    }
  }

  async handlePmosMemberUpsert() {
    this.pmosAdminError = null;
    upsertPmosMember(this);
    if (this.pmosAdminError) {
      return;
    }
    await this.handlePmosAdminSave({
      action: "pmos.admin.member.upsert",
      target: this.pmosMemberDraftEmail.trim().toLowerCase() || "member",
    });
    this.pmosMemberDraftName = "";
    this.pmosMemberDraftEmail = "";
    this.pmosMemberDraftRole = "member";
    this.pmosMemberDraftStatus = "active";
  }

  async handlePmosMemberRemove(email: string) {
    this.pmosAdminError = null;
    removePmosMember(this, email);
    if (this.pmosAdminError) {
      return;
    }
    await this.handlePmosAdminSave({
      action: "pmos.admin.member.remove",
      target: email.trim().toLowerCase(),
    });
  }

  async handlePmosIntegrationsLoad() {
    await loadConfig(this);
    await loadPmosConnectorsStatus(this);
    hydratePmosConnectorDraftsFromConfig(this);
    await loadPmosModelWorkspaceState(this);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
    await loadPmosWorkflowCredentials(this);

    // Load any existing workspace-scoped Wicked Ops connectors so the UI can reflect provisioning state
    try {
      if (this.client && this.connected) {
        const ws = await this.client.request<{ workspaceId: string; connectors: Record<string, any> }>(
          "pmos.connectors.workspace.get",
          {},
        );
        const ops = ws?.connectors?.ops ?? null;
        const opsUser = ops && typeof ops === "object" ? (ops as Record<string, unknown>).user : null;
        const opsUserObj =
          opsUser && typeof opsUser === "object" && !Array.isArray(opsUser)
            ? (opsUser as Record<string, unknown>)
            : null;
        this.pmosOpsUserEmailDraft =
          opsUserObj && typeof opsUserObj.email === "string" ? opsUserObj.email : "";
        this.pmosOpsUserHasSavedPassword = Boolean(
          opsUserObj?.hasPassword ||
            (typeof opsUserObj?.password === "string" && opsUserObj.password.length > 0),
        );
        this.pmosOpsUserPasswordDraft = "";
        if (ops && typeof ops === "object" && ops.apiKey) {
          const key = String(ops.apiKey);
          this.pmosOpsProvisioningResult = { projectId: ops.projectId ?? undefined, apiKey: key };
          this.pmosOpsManualApiKeyDraft = key;
          this.pmosOpsProvisioningError = null;
        } else {
          this.pmosOpsProvisioningResult = null;
          this.pmosOpsManualApiKeyDraft = "";
        }
      }
    } catch (err) {
      this.pmosOpsProvisioningResult = null;
    }
  }

  async handlePmosIntegrationsSave() {
    this.pmosBasecampSetupError = null;
    this.pmosBasecampSetupOk = false;
    await savePmosConnectorsConfig(this);
    this.flowConnectionsEmbedVersion = (this.flowConnectionsEmbedVersion ?? 0) + 1;
    await loadConfig(this);
    await loadPmosConnectorsStatus(this);
    this.pmosConnectorDraftsInitialized = false;
    hydratePmosConnectorDraftsFromConfig(this);
    await loadPmosModelWorkspaceState(this);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
    await loadPmosWorkflowCredentials(this);
    this.pmosBcgptSavedOk = true;
    setTimeout(() => { this.pmosBcgptSavedOk = false; }, 2500);
    if (this.pmosBasecampSetupOk) {
      setTimeout(() => { this.pmosBasecampSetupOk = false; }, 3000);
    }
  }

  async handlePmosSetupBasecampInWorkflowEngine() {
    if (!this.client || this.pmosBasecampSetupPending) return;
    this.pmosBasecampSetupPending = true;
    this.pmosBasecampSetupError = null;
    this.pmosBasecampSetupOk = false;
    try {
      try {
        await this.client.request("pmos.flow.setup.basecamp", {});
      } catch {
        await this.client.request("pmos.workflow.setup.basecamp", {});
      }
      this.pmosBasecampSetupOk = true;
      this.flowConnectionsEmbedVersion = (this.flowConnectionsEmbedVersion ?? 0) + 1;
      setTimeout(() => { this.pmosBasecampSetupOk = false; }, 3000);
    } catch (err) {
      this.pmosBasecampSetupError = err instanceof Error ? err.message : String(err);
    } finally {
      this.pmosBasecampSetupPending = false;
    }
  }

  async handleLoadRealCredentials() {
    if (!this.client || !this.connected) return;
    this.pmosRealCredentialsLoading = true;
    this.pmosRealCredentialsError = null;
    try {
      const res = await this.client.request<{ credentials: Array<{ id: string; name: string; type: string }> }>(
        "pmos.connections.list",
        {},
      );
      this.pmosRealCredentials = res?.credentials ?? [];
      if (
        this.pmosSelectedConnectionId &&
        !this.pmosRealCredentials.some((credential) => credential.id === this.pmosSelectedConnectionId)
      ) {
        this.pmosSelectedConnectionId = null;
      }
    } catch (err) {
      this.pmosRealCredentialsError = String(err);
      this.pmosRealCredentials = [];
      this.pmosSelectedConnectionId = null;
    } finally {
      this.pmosRealCredentialsLoading = false;
    }
  }

  async handlePmosIntegrationsClearBcgptKey() {
    this.pmosBasecampSetupError = null;
    this.pmosBasecampSetupOk = false;
    await savePmosConnectorsConfig(this, { clearBcgptKey: true });
    this.flowConnectionsEmbedVersion = (this.flowConnectionsEmbedVersion ?? 0) + 1;
    await loadConfig(this);
    await loadPmosConnectorsStatus(this);
    this.pmosConnectorDraftsInitialized = false;
    hydratePmosConnectorDraftsFromConfig(this);
    await loadPmosModelWorkspaceState(this);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
    await loadPmosWorkflowCredentials(this);
  }

  async handlePmosFigmaSyncContext(opts?: { auto?: boolean }) {
    if (!this.client || !this.connected) {
      if (!opts?.auto) {
        this.pmosFigmaContextError = "Connect to the gateway first.";
      }
      return;
    }

    const figmaBaseUrl = String(this.pmosFigmaUrl ?? "").trim().replace(/\/+$/, "") || "https://fm.wickedlab.io";
    this.pmosFigmaContextSyncing = true;
    this.pmosFigmaContextError = null;
    this.pmosFigmaContextSyncedOk = false;
    try {
      const response = await fetch(`${figmaBaseUrl}/api/pmos/context`, {
        credentials: "include",
        headers: {
          accept: "application/json",
        },
      });
      if (response.status === 401) {
        // Clear stale connected state so the iframe hides and sign-in CTA shows
        if (this.client) {
          await this.client.request("pmos.connectors.workspace.set", {
            connectors: {
              figma: {
                url: figmaBaseUrl,
                identity: { connected: false },
              },
            },
          }).catch(() => {});
        }
        await loadPmosConnectorsStatus(this);
        if (opts?.auto) {
          return;
        }
        throw new Error("Sign in to the Figma File Manager first, then retry sync.");
      }
      if (!response.ok) {
        throw new Error(`Figma context request failed (${response.status}).`);
      }

      const payload = (await response.json()) as {
        user?: { handle?: string | null; email?: string | null };
        auth?: {
          personalAccessToken?: string | null;
          hasPersonalAccessToken?: boolean | null;
          source?: string | null;
          mcpServerUrl?: string | null;
          fmMcpUrl?: string | null;
          fmMcpApiToken?: string | null;
          updatedAt?: string | null;
        } | null;
        activeConnection?: {
          id?: string | number | null;
          connection_name?: string | null;
          figma_team_id?: string | null;
          last_synced_at?: string | number | null;
          last_pat_validated_at?: string | number | null;
          updated_at?: string | null;
        } | null;
        selection?: {
          selectedFileUrl?: string | null;
          selectedFileId?: string | null;
          selectedFileName?: string | null;
          updatedAt?: string | null;
        } | null;
        connectionsCount?: number;
      };
      await this.persistPmosFigmaContext(figmaBaseUrl, payload);
      this.pmosFigmaLiveAuthVerified = true;
      this.pmosFigmaContextSyncedOk = true;
      setTimeout(() => {
        this.pmosFigmaContextSyncedOk = false;
      }, 2500);
    } catch (err) {
      this.pmosFigmaContextError = err instanceof Error ? err.message : String(err);
    } finally {
      this.pmosFigmaContextSyncing = false;
    }
  }

  async handlePmosProvisionOps(opts?: { projectName?: string }) {
    if (!this.client || !this.connected) {
      this.pmosOpsProvisioningError = "Not connected to gateway";
      return;
    }
    this.pmosOpsProvisioning = true;
    this.pmosOpsProvisioningError = null;
    this.pmosOpsProvisioningResult = null;
    try {
      const res = await this.client.request<{ ok?: boolean; projectId?: string; apiKey?: string }>(
        "pmos.connectors.workspace.provision_ops",
        { projectName: opts?.projectName ?? undefined },
      );
      // persist result in UI state and refresh connectors
      this.pmosOpsProvisioningResult = { projectId: res.projectId, apiKey: res.apiKey };
      this.pmosOpsProvisioningError = null;
      this.flowConnectionsEmbedVersion = (this.flowConnectionsEmbedVersion ?? 0) + 1;
      await this.handlePmosRefreshConnectors();
    } catch (err) {
      // keep original error message for display and allow manual-key fallback UI to show
      this.pmosOpsProvisioningError = String(err);
    } finally {
      this.pmosOpsProvisioning = false;
    }
  }

  async handlePmosSaveManualOpsKey() {
    if (!this.client || !this.connected) {
      this.pmosOpsProvisioningError = "Not connected to gateway";
      return;
    }
    const key = String(this.pmosOpsManualApiKeyDraft ?? "").trim();
    if (!key) {
      this.pmosOpsProvisioningError = "API key cannot be empty";
      return;
    }

    this.pmosOpsSavingManualKey = true;
    this.pmosOpsProvisioningError = null;
    try {
      await this.client.request("pmos.connectors.workspace.set", { connectors: { ops: { apiKey: key } } });
      // reflect saved key in UI state and refresh connectors
      this.pmosOpsProvisioningResult = { apiKey: key };
      this.pmosOpsManualApiKeyDraft = "";
      await this.handlePmosRefreshConnectors();
    } catch (err) {
      this.pmosOpsProvisioningError = String(err);
    } finally {
      this.pmosOpsSavingManualKey = false;
    }
  }

  private syncPmosModelDraftRef() {
    const modelId = this.pmosModelId.trim();
    if (!modelId) {
      this.pmosModelRefDraft = "";
      return;
    }
    this.pmosModelRefDraft = `${this.pmosModelProvider}/${modelId}`;
  }

  private syncPmosModelApiKeyEditability() {
    this.pmosModelApiKeyEditable =
      !this.pmosModelApiKeyStored || Boolean(this.pmosModelApiKeyDraft.trim());
  }

  private restorePmosModelEditor(modelRef?: string | null) {
    const target = modelRef?.trim() || this.pmosModelRefDraft.trim();
    if (target && selectPmosModelEditor(this, target)) {
      this.syncPmosModelApiKeyEditability();
      return;
    }
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  handlePmosModelProviderChange(next: PmosModelProvider) {
    setPmosModelProvider(this, next);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  handlePmosModelRefDraftChange(next: string) {
    const normalized = next.trim();
    this.pmosModelRefDraft = normalized;
    this.pmosModelError = null;
    const slash = normalized.indexOf("/");
    if (slash < 1) {
      return;
    }
    const provider = normalized.slice(0, slash).trim().toLowerCase();
    const modelId = normalized.slice(slash + 1).trim();
    if (!modelId) {
      return;
    }
    // When provider changes through the ref input, auto-fill URL/API type if empty or
    // if the current value matches the previous provider's default.
    if (this.pmosModelProvider !== provider) {
      setPmosModelProvider(this, provider as PmosModelProvider);
      this.syncPmosModelApiKeyEditability();
    }
    this.pmosModelProvider = provider as PmosModelProvider;
    this.pmosModelId = modelId;
    this.syncPmosModelApiKeyEditability();
  }

  handlePmosModelEdit(modelRef: string) {
    if (!selectPmosModelEditor(this, modelRef)) {
      this.pmosModelError = "Unable to load that model into the editor.";
      return;
    }
    this.syncPmosModelApiKeyEditability();
  }

  handlePmosModelApiKeyEditToggle(enabled: boolean) {
    this.pmosModelApiKeyEditable = enabled;
    if (!enabled) {
      this.pmosModelApiKeyDraft = "";
    }
  }

  async handlePmosModelSave() {
    const ref = this.pmosModelRefDraft.trim();
    if (ref) {
      await upsertPmosModelFromRef(this, {
        modelRef: ref,
        alias: this.pmosModelAlias,
        apiKey: this.pmosModelApiKeyDraft,
        baseUrl: this.pmosModelBaseUrl,
        apiType: this.pmosModelApiType,
        activate: true,
      });
    } else {
      await savePmosModelConfig(this);
    }
    if (!this.pmosModelError) {
      this.restorePmosModelEditor(ref || null);
      this.pmosModelSavedOk = true;
      setTimeout(() => { this.pmosModelSavedOk = false; }, 2500);
      return;
    }
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  async handlePmosModelSaveWithoutActivate() {
    const ref = this.pmosModelRefDraft.trim();
    if (!ref) {
      this.pmosModelError = "Select or enter a model first.";
      return;
    }
    await upsertPmosModelFromRef(this, {
      modelRef: ref,
      alias: this.pmosModelAlias,
      apiKey: this.pmosModelApiKeyDraft,
      baseUrl: this.pmosModelBaseUrl,
      apiType: this.pmosModelApiType,
      activate: false,
    });
    if (!this.pmosModelError) {
      this.restorePmosModelEditor(ref);
      this.pmosModelSavedOk = true;
      setTimeout(() => { this.pmosModelSavedOk = false; }, 2500);
      return;
    }
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  async handlePmosModelActivate(modelRef: string) {
    await activatePmosModel(this, modelRef);
    this.restorePmosModelEditor(modelRef);
  }

  async handlePmosModelDeactivate(modelRef: string) {
    await deactivatePmosModel(this, modelRef);
    this.restorePmosModelEditor(modelRef);
  }

  async handlePmosModelDelete(modelRef: string) {
    await deletePmosModel(this, modelRef);
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  async handlePmosModelClearKey() {
    const currentRef = this.pmosModelRefDraft.trim();
    await clearPmosModelApiKey(this);
    await loadPmosModelWorkspaceState(this);
    this.restorePmosModelEditor(currentRef || null);
    this.pmosModelApiKeyEditable = true;
  }

  async handlePmosModelClearKeyForRef(modelRef: string) {
    await clearPmosModelApiKeyForRef(this, modelRef);
    await loadPmosModelWorkspaceState(this);
    this.restorePmosModelEditor(modelRef);
    this.pmosModelApiKeyEditable = true;
  }

  async handlePmosAssignAgentModel(agentId: string, modelRef: string | null) {
    await assignPmosAgentModel(this, { agentId, modelRef });
    this.syncPmosModelDraftRef();
    this.syncPmosModelApiKeyEditability();
  }

  async handlePmosApPiecesLoad() {
    await loadWorkflowPieces(this as unknown as Parameters<typeof loadWorkflowPieces>[0]);
  }

  async handlePmosApConnectionsLoad() {
    await loadWorkflowConnections(
      this as unknown as Parameters<typeof loadWorkflowConnections>[0],
    );
  }

  async handlePmosApConnectionCreate() {
    await createWorkflowConnection(
      this as unknown as Parameters<typeof createWorkflowConnection>[0],
    );
  }

  async handlePmosApConnectionDelete(connectionId: string) {
    await deleteWorkflowConnection(
      this as unknown as Parameters<typeof deleteWorkflowConnection>[0],
      connectionId,
    );
  }

  async handlePmosApFlowsLoad() {
    await loadWorkflows(this as unknown as Parameters<typeof loadWorkflows>[0]);
  }

  async handlePmosApFlowCreate() {
    await createWorkflow(this as unknown as Parameters<typeof createWorkflow>[0]);
  }

  async handlePmosApFlowSelect(flowId: string) {
    await loadWorkflowDetails(
      this as unknown as Parameters<typeof loadWorkflowDetails>[0],
      flowId,
    );
  }

  async handlePmosApFlowRename() {
    await renameWorkflow(this as unknown as Parameters<typeof renameWorkflow>[0]);
  }

  async handlePmosApFlowSetStatus(status: "ENABLED" | "DISABLED") {
    await setWorkflowStatus(
      this as unknown as Parameters<typeof setWorkflowStatus>[0],
      status,
    );
  }

  async handlePmosApFlowPublish() {
    await publishWorkflow(
      this as unknown as Parameters<typeof publishWorkflow>[0],
    );
  }

  async handlePmosApFlowDelete() {
    await deleteWorkflow(this as unknown as Parameters<typeof deleteWorkflow>[0]);
  }

  async handlePmosApFlowApplyOperation() {
    await applyWorkflowOperationDraft(
      this as unknown as Parameters<typeof applyWorkflowOperationDraft>[0],
    );
  }

  async handlePmosApFlowTriggerWebhook(opts?: { draft?: boolean; sync?: boolean }) {
    await triggerWorkflowWebhook(
      this as unknown as Parameters<typeof triggerWorkflowWebhook>[0],
      opts,
    );
  }

  async handlePmosFlowBuilderGenerate() {
    await generatePmosFlowBuilderPlan(
      this as unknown as Parameters<typeof generatePmosFlowBuilderPlan>[0],
    );
  }

  async handlePmosFlowBuilderCommit() {
    await commitPmosFlowBuilderPlan(
      this as unknown as Parameters<typeof commitPmosFlowBuilderPlan>[0],
    );
  }

  handlePmosFlowBuilderReset() {
    resetPmosFlowBuilder(this as unknown as Parameters<typeof resetPmosFlowBuilder>[0]);
  }

  async handleWorkflowChatSend() {
    const message = this.workflowChatDraft.trim();
    if (!message || this.workflowChatSending) return;
    this.workflowChatDraft = "";
    this.workflowChatStreamStartedAt = Date.now();
    // Start with empty stream (reading indicator) — server drives steps/tokens via progress events
    this.workflowChatStream = "";
    this.workflowChatSteps = [];

    const history = [...this.workflowChatMessages, { role: "user" as const, content: message }];
    this.workflowChatMessages = history;
    this.workflowChatSending = true;

    try {
      const result = await this.client!.request("pmos.workflow.assist", {
        messages: history.map(m => ({ role: m.role, content: m.content })),
        currentWorkflowId: this.apFlowSelectedId ?? undefined,
      }) as {
        ok: boolean;
        message: string;
        workflowCreated?: boolean;
        workflowId?: string;
        workflowName?: string;
        providerUsed?: string;
      };

      const reply = result.message || "I couldn't process that.";
      this.workflowChatMessages = [...this.workflowChatMessages, { role: "assistant", content: reply }];

      // workflow_ready progress event already navigated the iframe during streaming;
      // fallback: if server returned workflowId but event wasn't received, navigate now
      if (result.workflowCreated && result.workflowId && this.apFlowSelectedId !== result.workflowId) {
        this.apFlowSelectedId = result.workflowId;
        this.workflowEmbedVersion = (this.workflowEmbedVersion ?? 0) + 1;
      }
      if (result.workflowCreated) {
        void this.handlePmosApFlowsLoad();
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      this.workflowChatMessages = [...this.workflowChatMessages, { role: "assistant", content: `Error: ${errMsg}` }];
    } finally {
      this.workflowChatSending = false;
      window.setTimeout(() => {
        if (this.workflowChatSending) {
          return;
        }
        this.workflowChatStream = null;
        this.workflowChatStreamStartedAt = null;
      }, 400);
    }
  }

  async handleWorkflowChatConfirm() {
    const pending = this.workflowChatPendingWorkflow;
    if (!pending || !this.client) return;
    this.workflowChatPendingWorkflow = null;
    this.workflowChatSending = true;
    try {
      const created = await this.client.request("pmos.workflow.confirm", {
        workflow: pending,
        confirmed: true,
      }) as { success: boolean; workflowId?: string; message?: string };
      if (created.success && created.workflowId) {
        this.workflowChatMessages = [
          ...this.workflowChatMessages,
          { role: "assistant", content: `Workflow "${pending.name}" created. Open it in the Activepieces editor to configure credentials and test it. (ID: ${String(created.workflowId).slice(0, 8)}...)` },
        ];
        void this.handlePmosApFlowsLoad();
      } else {
        this.workflowChatMessages = [
          ...this.workflowChatMessages,
          { role: "assistant", content: `Workflow creation failed: ${created.message || "Unknown error"}` },
        ];
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      this.workflowChatMessages = [
        ...this.workflowChatMessages,
        { role: "assistant", content: `Workflow creation failed: ${errMsg}` },
      ];
    } finally {
      this.workflowChatSending = false;
    }
  }

  handleWorkflowChatCancelWorkflow() {
    this.workflowChatPendingWorkflow = null;
    this.workflowChatMessages = [
      ...this.workflowChatMessages,
      { role: "assistant", content: "Workflow cancelled. Let me know if you'd like to adjust anything." },
    ];
  }

  async handlePmosApRunsLoad() {
    await loadWorkflowRuns(this as unknown as Parameters<typeof loadWorkflowRuns>[0]);
  }

  async handlePmosApRunSelect(runId: string) {
    await loadWorkflowRunDetails(
      this as unknown as Parameters<typeof loadWorkflowRunDetails>[0],
      runId,
    );
  }

  async handlePmosApRunRetry(strategy: "FROM_FAILED_STEP" | "ON_LATEST_VERSION") {
    await retryWorkflowRun(
      this as unknown as Parameters<typeof retryWorkflowRun>[0],
      strategy,
    );
  }

  async handlePmosCommandPlan() {
    await planPmosCommand(this as unknown as Parameters<typeof planPmosCommand>[0]);
  }

  async handlePmosCommandExecute() {
    await executePmosCommandPlan(
      this as unknown as Parameters<typeof executePmosCommandPlan>[0],
    );
  }

  async handlePmosCommandApprove(approvalId: string) {
    await approvePmosCommandStep(
      this as unknown as Parameters<typeof approvePmosCommandStep>[0],
      approvalId,
    );
  }

  async handlePmosProjectsLoad(options?: { fresh?: boolean }) {
    await loadPmosProjectsSnapshot(
      this as unknown as Parameters<typeof loadPmosProjectsSnapshot>[0],
      options,
    );
    if (this.pmosSelectedProject && this.pmosProjectsSnapshot) {
      const refreshedProject = this.pmosProjectsSnapshot.projects.find(
        (project) =>
          project.id === this.pmosSelectedProject?.id ||
          project.name === this.pmosSelectedProject?.name,
      );
      if (refreshedProject) {
        this.pmosSelectedProject = refreshedProject;
        if (this.pmosProjectDetailTab === "overview") {
          this.pmosScreenContext = buildProjectScreenContext(refreshedProject, "overview", null);
        }
      }
    }
    // Auto-inject screen context after snapshot loads
    if (!this.pmosSelectedProject && this.pmosProjectsSnapshot) {
      this.pmosScreenContext = this.pmosCommandCenterTab === "projects"
        ? buildProjectsListScreenContext(this.pmosProjectsSnapshot, this.pmosProjectSearch)
        : buildOverviewScreenContext(this.pmosProjectsSnapshot);
    }
  }

  handleCommandCenterTabChange(tab: "overview" | "projects" | "timeline") {
    this.pmosCommandCenterTab = tab;
    if (!this.pmosSelectedProject) {
      if (tab === "overview") {
        this.pmosScreenContext = buildOverviewScreenContext(this.pmosProjectsSnapshot);
      } else if (tab === "projects") {
        this.pmosScreenContext = buildProjectsListScreenContext(this.pmosProjectsSnapshot, this.pmosProjectSearch);
      } else {
        this.pmosScreenContext = JSON.stringify({ view: "command-center", tab: "timeline", totals: this.pmosProjectsSnapshot?.totals ?? {} });
      }
    }
  }

  handleSelectProject(project: PmosProjectCard | null) {
    this.pmosSelectedProject = project;
    this.pmosProjectDetailTab = "overview";
    this.pmosSelectedEntityRef = null;
    this.pmosSelectedEntityDetail = null;
    this.pmosSelectedEntityLoading = false;
    this.pmosSelectedEntityError = null;
    this.pmosProjectActionError = null;
    this.pmosProjectActionMessage = null;
    this.pmosTodoDraftTitle = "";
    this.pmosTodoDraftDescription = "";
    this.pmosTodoDraftList = "";
    this.pmosTodoDraftDueOn = "";
    this.pmosEntityCommentDraft = "";
    if (project) {
      this.pmosScreenContext = buildProjectScreenContext(project, "overview", null);
    } else {
      // Going back to project list -- set overview context
      this.pmosScreenContext = this.pmosCommandCenterTab === "projects"
        ? buildProjectsListScreenContext(this.pmosProjectsSnapshot, this.pmosProjectSearch)
        : buildOverviewScreenContext(this.pmosProjectsSnapshot);
    }
  }

  handleProjectDetailTabChange(tab: PmosProjectDetailTab) {
    this.pmosProjectDetailTab = tab;
    this.pmosSelectedEntityRef = null;
    this.pmosSelectedEntityDetail = null;
    this.pmosSelectedEntityLoading = false;
    this.pmosSelectedEntityError = null;
    this.pmosProjectActionError = null;
    this.pmosProjectActionMessage = null;
    const project = this.pmosSelectedProject;
    if (!project) { this.pmosScreenContext = null; return; }
    if (tab === "overview") {
      this.pmosScreenContext = buildProjectScreenContext(project, "overview", null);
    } else {
      const key = `${project.id}:${tab}`;
      const sectionData = this.pmosProjectSectionData[key];
      this.pmosScreenContext = buildProjectScreenContext(project, tab, sectionData?.data ?? null);
    }
  }

  async handleLoadProjectSection(projectName: string, section: PmosProjectDetailTab) {
    if (!this.client || section === "overview") return;
    const key = `${this.pmosSelectedProject?.id ?? projectName}:${section}`;
    // Don't re-fetch if already loading
    if (this.pmosProjectSectionData[key]?.loading) return;
    this.pmosProjectSectionData = {
      ...this.pmosProjectSectionData,
      [key]: { loading: true, error: null, data: null },
    };
    try {
      const data = await fetchProjectSection(this.client, projectName, section);
      this.pmosProjectSectionData = {
        ...this.pmosProjectSectionData,
        [key]: { loading: false, error: null, data },
      };
      // Update screen context with rich data summary if still on this tab
      if (this.pmosSelectedProject && this.pmosProjectDetailTab === section) {
        this.pmosScreenContext = buildProjectScreenContext(this.pmosSelectedProject, section, data);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.pmosProjectSectionData = {
        ...this.pmosProjectSectionData,
        [key]: { loading: false, error: message, data: null },
      };
    }
  }

  async handleOpenProjectEntity(reference: PmosEntityReference) {
    if (!this.client) return;
    this.pmosSelectedEntityRef = reference;
    this.pmosSelectedEntityLoading = true;
    this.pmosSelectedEntityError = null;
    this.pmosProjectActionError = null;
    this.pmosProjectActionMessage = null;
    this.pmosEntityCommentDraft = "";
    try {
      const detail = await fetchProjectEntityDetail(this.client, reference);
      if (
        this.pmosSelectedEntityRef?.type !== reference.type ||
        this.pmosSelectedEntityRef?.id !== reference.id ||
        this.pmosSelectedEntityRef?.url !== reference.url
      ) {
        return;
      }
      this.pmosSelectedEntityDetail = {
        ...detail,
        reference: {
          ...detail.reference,
          label: reference.label ?? detail.reference.label,
        },
      };
      this.pmosSelectedEntityLoading = false;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.pmosSelectedEntityDetail = null;
      this.pmosSelectedEntityLoading = false;
      this.pmosSelectedEntityError = message;
    }
  }

  handleCloseProjectEntity() {
    this.pmosSelectedEntityRef = null;
    this.pmosSelectedEntityDetail = null;
    this.pmosSelectedEntityLoading = false;
    this.pmosSelectedEntityError = null;
    this.pmosProjectActionError = null;
    this.pmosProjectActionMessage = null;
    this.pmosEntityCommentDraft = "";
  }

  handlePmosTodoDraftChange(field: "title" | "description" | "list" | "dueOn", value: string) {
    if (field === "title") this.pmosTodoDraftTitle = value;
    else if (field === "description") this.pmosTodoDraftDescription = value;
    else if (field === "list") this.pmosTodoDraftList = value;
    else this.pmosTodoDraftDueOn = value;
  }

  handlePmosEntityCommentDraftChange(value: string) {
    this.pmosEntityCommentDraft = value;
  }

  private async refreshPmosProjectSurfaces(options: {
    section?: PmosProjectDetailTab | null;
    reopenEntity?: boolean;
  } = {}) {
    const project = this.pmosSelectedProject;
    if (!this.client || !project) return;

    const section = options.section ?? this.pmosProjectDetailTab;
    if (section && section !== "overview") {
      const key = `${project.id}:${section}`;
      this.pmosProjectSectionData = {
        ...this.pmosProjectSectionData,
        [key]: { loading: true, error: null, data: this.pmosProjectSectionData[key]?.data ?? null },
      };
      try {
        const data = await fetchProjectSection(this.client, project.name, section);
        this.pmosProjectSectionData = {
          ...this.pmosProjectSectionData,
          [key]: { loading: false, error: null, data },
        };
        if (this.pmosSelectedProject && this.pmosProjectDetailTab === section) {
          this.pmosScreenContext = buildProjectScreenContext(this.pmosSelectedProject, section, data);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.pmosProjectSectionData = {
          ...this.pmosProjectSectionData,
          [key]: { loading: false, error: message, data: this.pmosProjectSectionData[key]?.data ?? null },
        };
      }
    }

    await loadPmosProjectsSnapshot(this as unknown as Parameters<typeof loadPmosProjectsSnapshot>[0]);
    if (this.pmosSelectedProject && this.pmosProjectsSnapshot) {
      const refreshedProject = this.pmosProjectsSnapshot.projects.find(
        (candidate) =>
          candidate.id === this.pmosSelectedProject?.id ||
          candidate.name === this.pmosSelectedProject?.name,
      );
      if (refreshedProject) {
        this.pmosSelectedProject = refreshedProject;
        if (this.pmosProjectDetailTab === "overview") {
          this.pmosScreenContext = buildProjectScreenContext(refreshedProject, "overview", null);
        }
      }
    }

    if (options.reopenEntity && this.pmosSelectedEntityRef) {
      await this.handleOpenProjectEntity(this.pmosSelectedEntityRef);
    }
  }

  private async runPmosMutation(
    operation: () => Promise<PmosMutationResult>,
    options: { refreshSection?: PmosProjectDetailTab | null; reopenEntity?: boolean } = {},
  ): Promise<PmosMutationResult | null> {
    this.pmosProjectActionBusy = true;
    this.pmosProjectActionError = null;
    this.pmosProjectActionMessage = null;
    try {
      const result = await operation();
      this.pmosProjectActionMessage = result.message;
      await this.refreshPmosProjectSurfaces({
        section: options.refreshSection ?? this.pmosProjectDetailTab,
        reopenEntity: options.reopenEntity ?? false,
      });
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.pmosProjectActionError = message;
      return null;
    } finally {
      this.pmosProjectActionBusy = false;
    }
  }

  async handleCreateProjectTodo() {
    if (!this.client || !this.pmosSelectedProject) return;
    const title = this.pmosTodoDraftTitle.trim();
    if (!title) {
      this.pmosProjectActionError = "Todo title is required.";
      return;
    }
    const result = await this.runPmosMutation(
      () =>
        createProjectTodo(this.client!, {
          projectName: this.pmosSelectedProject!.name,
          title,
          description: this.pmosTodoDraftDescription.trim() || null,
          todolist: this.pmosTodoDraftList.trim() || null,
          dueOn: this.pmosTodoDraftDueOn || null,
        }),
      { refreshSection: "todos" },
    );
    if (!result) return;
    this.pmosProjectDetailTab = "todos";
    this.pmosTodoDraftTitle = "";
    this.pmosTodoDraftDescription = "";
    this.pmosTodoDraftList = "";
    this.pmosTodoDraftDueOn = "";
  }

  async handleToggleProjectTodo(todoId: string, completed: boolean) {
    if (!this.client || !this.pmosSelectedProject || !todoId) return;
    await this.runPmosMutation(
      () =>
        completed
          ? reopenProjectTodo(this.client!, {
              projectName: this.pmosSelectedProject!.name,
              todoId,
            })
          : completeProjectTodo(this.client!, {
              projectName: this.pmosSelectedProject!.name,
              todoId,
            }),
      { refreshSection: "todos", reopenEntity: true },
    );
  }

  async handleCreateProjectComment() {
    if (!this.client || !this.pmosSelectedProject || !this.pmosSelectedEntityRef) return;
    const content = this.pmosEntityCommentDraft.trim();
    if (!content) {
      this.pmosProjectActionError = "Comment text is required.";
      return;
    }
    const result = await this.runPmosMutation(
      () =>
        createProjectComment(this.client!, {
          projectName: this.pmosSelectedProject!.name,
          reference: this.pmosSelectedEntityRef!,
          content,
        }),
      { refreshSection: this.pmosProjectDetailTab, reopenEntity: true },
    );
    if (!result) return;
    this.pmosEntityCommentDraft = "";
  }

  handlePmosCommandClearHistory() {
    clearPmosCommandHistory(this);
  }

  removeQueuedMessage(id: string) {
    removeQueuedMessageInternal(
      this as unknown as Parameters<typeof removeQueuedMessageInternal>[0],
      id,
    );
  }

  async handleSendChat(
    messageOverride?: string,
    opts?: Parameters<typeof handleSendChatInternal>[2],
  ) {
    await handleSendChatInternal(
      this as unknown as Parameters<typeof handleSendChatInternal>[0],
      messageOverride,
      opts,
    );
  }

  async handleChatCreateWorkflow() {
    const draft = (this.chatMessage ?? "").trim();
    if (!draft) {
      this.lastError = "Type a message first to create a workflow.";
      return;
    }

    // Simple title: first 6 words title-cased
    const words = draft.split(/\s+/).filter(Boolean).slice(0, 6);
    const title = (words.length ? words.join(" ") : "New Automation").slice(0, 64);
    const name = title.replace(/\b\w/g, (c) => c.toUpperCase());

    this.chatCreateWorkflowBusy = true;
    this.chatCreateWorkflowError = null;
    try {
      // Lazy import controller to avoid circular deps
      const mod = await import("./controllers/wicked-ops.js");
      const res = await mod.generateN8nWorkflow(
        this as unknown as Parameters<typeof mod.generateN8nWorkflow>[0],
        name,
        draft,
      );

      const details = (res ?? {}) as Record<string, unknown>;
      const possibleId =
        details.id ?? details.workflowId ?? (details.data && (details.data as any).id) ??
        (details.details && (details.details as any).id);
      const workflowId = possibleId ? String(possibleId) : undefined;

      // Keep workflow editing native inside the dashboard tab.
      if (workflowId) {
        this.apFlowSelectedId = workflowId;
        this.setTab("automations");
        return;
      }

      this.lastError =
        "Workflow created but ID could not be detected. Opening Workflows tab.";
      this.setTab("automations");
    } catch (err) {
      this.chatCreateWorkflowError = String(err ?? "unknown error");
      this.lastError = `Create workflow failed: ${String(err)}`;
    } finally {
      this.chatCreateWorkflowBusy = false;
    }
  }

  async handleWhatsAppStart(force: boolean) {
    await handleWhatsAppStartInternal(this, force);
  }

  async handleWhatsAppWait() {
    await handleWhatsAppWaitInternal(this);
  }

  async handleWhatsAppLogout() {
    await handleWhatsAppLogoutInternal(this);
  }

  async handleChannelConfigSave() {
    await handleChannelConfigSaveInternal(this);
  }

  async handleChannelConfigReload() {
    await handleChannelConfigReloadInternal(this);
  }

  handleNostrProfileEdit(accountId: string, profile: NostrProfile | null) {
    handleNostrProfileEditInternal(this, accountId, profile);
  }

  handleNostrProfileCancel() {
    handleNostrProfileCancelInternal(this);
  }

  handleNostrProfileFieldChange(field: keyof NostrProfile, value: string) {
    handleNostrProfileFieldChangeInternal(this, field, value);
  }

  async handleNostrProfileSave() {
    await handleNostrProfileSaveInternal(this);
  }

  async handleNostrProfileImport() {
    await handleNostrProfileImportInternal(this);
  }

  handleNostrProfileToggleAdvanced() {
    handleNostrProfileToggleAdvancedInternal(this);
  }

  async handleExecApprovalDecision(decision: "allow-once" | "allow-always" | "deny") {
    const active = this.execApprovalQueue[0];
    if (!active || !this.client || this.execApprovalBusy) {
      return;
    }
    this.execApprovalBusy = true;
    this.execApprovalError = null;
    try {
      await this.client.request("exec.approval.resolve", {
        id: active.id,
        decision,
      });
      this.execApprovalQueue = this.execApprovalQueue.filter((entry) => entry.id !== active.id);
    } catch (err) {
      this.execApprovalError = `Exec approval failed: ${String(err)}`;
    } finally {
      this.execApprovalBusy = false;
    }
  }

  handleGatewayUrlConfirm() {
    const nextGatewayUrl = this.pendingGatewayUrl;
    if (!nextGatewayUrl) {
      return;
    }
    this.pendingGatewayUrl = null;
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], {
      ...this.settings,
      gatewayUrl: nextGatewayUrl,
    });
    this.connect();
  }

  handleGatewayUrlCancel() {
    this.pendingGatewayUrl = null;
  }

  // Sidebar handlers for tool output viewing
  handleOpenSidebar(content: string) {
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
      this.sidebarCloseTimer = null;
    }
    this.sidebarContent = content;
    this.sidebarError = null;
    this.sidebarOpen = true;
  }

  handleCloseSidebar() {
    this.sidebarOpen = false;
    // Clear content after transition
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
    }
    this.sidebarCloseTimer = window.setTimeout(() => {
      if (this.sidebarOpen) {
        return;
      }
      this.sidebarContent = null;
      this.sidebarError = null;
      this.sidebarCloseTimer = null;
    }, 200);
  }

  handleSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.4, Math.min(0.7, ratio));
    this.splitRatio = newRatio;
    this.applySettings({ ...this.settings, splitRatio: newRatio });
  }

  handleAutomationsLeftPanelRatioChange(ratio: number) {
    const newRatio = Math.max(0.18, Math.min(0.42, ratio));
    this.automationsLeftPanelRatio = newRatio;
  }

  handleAutomationsCenterSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.45, Math.min(0.85, ratio));
    this.automationsCenterSplitRatio = newRatio;
  }

  render() {
    return renderApp(this as unknown as AppViewState);
  }
}
